import type { PlantEntityMeta, PlantProvenance } from '../types'

const PLANT_PROVENANCE_KEYS = [
  'source_file',
  'source_kind',
  'source_page',
  'source_region',
  'source_excerpt',
  'extraction_model',
  'extraction_method',
  'confidence',
  'evidence_items',
  'last_evidence_at'
] as const satisfies readonly (keyof PlantProvenance)[]

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined
  return value as Record<string, unknown>
}

export function coercePlantProvenance(value: unknown): PlantProvenance | undefined {
  let decoded = value
  if (typeof value === 'string') {
    try {
      decoded = JSON.parse(value) as unknown
    } catch {
      return undefined
    }
  }

  const raw = asRecord(decoded)
  if (!raw) return undefined

  const picked: Record<string, unknown> = {}
  for (const key of PLANT_PROVENANCE_KEYS) {
    if (raw[key] !== undefined) picked[key] = raw[key]
  }
  return Object.keys(picked).length > 0 ? (picked as PlantProvenance) : undefined
}

export function normalizePlantConfidence(raw: Record<string, unknown>): number | undefined {
  const provenance = coercePlantProvenance(raw['provenance'])
  const candidates: unknown[] = [raw['confidence'], provenance?.confidence, raw['llm_confidence']]
  const value = candidates.find((candidate) => candidate !== undefined)
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return Math.min(1, Math.max(0, value))
}

export function extractPlantMeta(raw: Record<string, unknown>): PlantEntityMeta {
  return {
    provenance: coercePlantProvenance(raw['provenance']),
    valid: typeof raw['valid'] === 'boolean' ? raw['valid'] : undefined,
    confidence: normalizePlantConfidence(raw),
    lastIngestJobId:
      typeof raw['lastIngestJobId'] === 'string' && raw['lastIngestJobId'].length > 0
        ? raw['lastIngestJobId']
        : undefined
  }
}
